"use client";

import React, { useEffect, useState, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { useHistory } from "react-router-dom";
import { apiService } from "lib/apiService";

export default function ExpenseTreeChart({ years, currency }) {
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);
  const [isDark, setIsDark] = useState(false);
  const [roamEnabled, setRoamEnabled] = useState(false);

  const history = useHistory();

  // Redirect if not logged in
  useEffect(() => {
    if (!localStorage.getItem("userData")) {
      history.push("/login");
    }
  }, [history]);

  const storedUserData = localStorage.getItem("userData");
  let groupId = "";
  if (storedUserData) {
    const userData = JSON.parse(storedUserData);
    groupId = userData.groupId;
  }

  // Detect dark mode
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    setIsDark(document.documentElement.classList.contains("dark"));
    return () => observer.disconnect();
  }, []);

  // Fetch Tree Data
  useEffect(() => {
    async function fetchData() {
      if (!groupId || years.length === 0) return;

      try {
        const data = await apiService.getTreeGraphData(
          groupId,
          years,
          currency
        );

        const nested = buildTree(data, isDark);
        setTreeData(nested);
      } catch (err) {
        console.error("Failed to load tree data", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [groupId, years, isDark]);

  // Build Tree
  function buildTree(flat, isDarkTheme) {
    const idMap = {};
    const roots = [];

    flat.forEach((node) => {
      const level = node.id.split("-").length - 1;

      let symbol = "circle";
      let customColor = "#9ca3af";

      if (node.id === "root") {
        symbol = "diamond";
        customColor = isDarkTheme ? "#fb8b24" : "#e36414";
      } else if (node.parent === "root") {
        symbol = "rect";
        customColor = isDarkTheme ? "#48cae4" : "#0077b6";
      } else if (level === 1) {
        symbol = "circle";
        customColor = isDarkTheme ? "#a7c957" : "#6a994e";
      } else {
        symbol = "diamond";
        customColor = isDarkTheme ? "#89c2d9" : "#02c39a";
      }

      idMap[node.id] = {
        ...node,
        children: [],
        symbol,
        symbolSize: 14,
        itemStyle: { color: customColor },
      };
    });

    flat.forEach((node) => {
      if (node.parent && idMap[node.parent]) {
        idMap[node.parent].children.push(idMap[node.id]);
      } else {
        roots.push(idMap[node.id]);
      }
    });

    return roots;
  }

  // ECharts Options
  const option = {
    backgroundColor: isDark ? "#1f2937" : "#ffffff",
    tooltip: {
      trigger: "item",
      backgroundColor: isDark ? "#374151" : "#f9fafb",
      borderColor: isDark ? "#4b5563" : "#e5e7eb",
      textStyle: { color: isDark ? "#f9fafb" : "#111827" },
      formatter: (params) => `<b>${params.data.name}</b>`,
    },
    series: [
      {
        type: "tree",
        data: treeData,
        layout: "orthogonal",
        orient: "LR",
        top: "2%",
        left: "15%",
        bottom: "0%",
        right: "15%",
        symbolSize: 14,

        label: {
          position: "left",
          align: "right",
          color: isDark ? "#f9fafb" : "#111827",
          fontSize: 13,
        },
        leaves: {
          label: {
            position: "right",
            align: "left",
            color: isDark ? "#f9fafb" : "#111827",
            fontSize: 13,
          },
        },

        lineStyle: { color: isDark ? "#6b7280" : "#d1d5db" },
        expandAndCollapse: true,
        emphasis: { focus: "descendant" },
        initialTreeDepth: 2,
        animationDuration: 350,
        animationDurationUpdate: 350,
        roam: roamEnabled,
        nodePadding: 25,
      },
    ],
  };

  // Hover Expand + Collapse
  const onChartReady = (chart) => {
    chartRef.current = chart;

    chart.on("mouseover", (params) => {
      const level = params.data.id.split("-").length - 1;
      if (level === 1) {
        chart.dispatchAction({
          type: "expandTreeNode",
          seriesIndex: 0,
          dataIndex: params.dataIndex,
        });
      }
    });

    chart.on("mouseout", (params) => {
      const level = params.data.id.split("-").length - 1;
      if (level === 1) {
        chart.dispatchAction({
          type: "collapseTreeNode",
          seriesIndex: 0,
          dataIndex: params.dataIndex,
        });
      }
    });
  };

  // Loading UI — EXACT same as your Next.js Card skeleton
  if (loading) {
    return (
      <div className="shadow-lg border-0 bg-white dark:bg-gray-800 rounded-lg p-6 col-span-3">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Yearly Credit & Debit Overview
        </h2>
        <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded-md w-full h-[650px]" />
      </div>
    );
  }

  // Final UI — EXACT replica
  return (
    <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4 col-span-full">
      <div
        className="flex justify-between mb-2 flex-col lg:flex-row"
        style={{ alignItems: "flex-start" }} // ✅ mobile left align
      >
        <h2
          className="text-base font-semibold text-gray-900 dark:text-gray-100"
          style={{ textAlign: "left", width: "100%" }} // ✅ force left
        >
          Yearly Credit & Debit Overview
        </h2>

        <div
          className="flex items-center gap-4"
          style={{ alignSelf: "flex-start", marginTop: "8px" }} // ✅ mobile left
        >
          {/* Move Chart Toggle */}
          {/* Toggle Label */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: "140px" }}>
            <span
              style={{
                fontSize: "14px",
                color: isDark ? "#d1d5db" : "#374151",
              }}
            >
              Move chart
            </span>

            {/* Toggle Switch */}
            <div
              onClick={() => setRoamEnabled(!roamEnabled)}
              style={{
                width: "56px",
                height: "28px",
                display: "flex",
                alignItems: "center",
                padding: "4px",
                borderRadius: "9999px",
                cursor: "pointer",
                transition: "background-color 0.3s ease",
                backgroundColor: roamEnabled ? "#3B82F6" : "#9CA3AF", // blue-500 or gray-400
              }}
            >
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  backgroundColor: "#fff",
                  borderRadius: "9999px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                  transform: roamEnabled ? "translateX(28px)" : "translateX(0px)",
                  transition: "transform 0.3s ease",
                }}
              ></div>
            </div>
          </div>

          {/* View Map Button */}
          <button
            onClick={() => window.open("/detaild-tree-map", "")}
            style={{ minWidth: "150px" }}
            className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition"
          >
            View Detailed Map
          </button>
        </div>
      </div>

      <div className="p-6">
        <div style={{ width: "100%", overflowX: "auto" }}>
          <div style={{ minWidth: "700px" }}>
            <ReactECharts
              option={option}
              style={{ height: "950px", width: "100%" }}
              onChartReady={onChartReady}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
